import  { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import '../css/RfmTableOffcanvas.css'
import TableTransactionsTab from './TableTransactionsTab';
import formatOrderCode from '../js/formatOrderCode';
import ElapsedClock from './ElapsedClock';
/* iconos mínimos para el toggle y flechas (no reemplazan tus bi bi-*) */
const IcoWindow = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M3 9h18" /></svg>);
const IcoDock = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M15 4v16" /></svg>);
const IcoChevL = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>);
const IcoChevR = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>);

/* flechas de scroll para las tabs */
const EPS = 2;
function useScrollArrows(ref) {
  const [state, setState] = useState({ showArrows: false, canPrev: false, canNext: false });
  const recheck = useCallback(() => {
    const el = ref.current; if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const overflow = max > EPS;
    setState({ showArrows: overflow, canPrev: overflow && el.scrollLeft > EPS, canNext: overflow && el.scrollLeft < max - EPS });
  }, [ref]);
  const scrollBy = useCallback((dir) => {
    const el = ref.current; if (!el) return;
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.6), behavior: "smooth" });
    setTimeout(recheck, 320);
  }, [ref, recheck]);
  const scrollPrev = useCallback(() => scrollBy(-1), [scrollBy]);
  const scrollNext = useCallback(() => scrollBy(1), [scrollBy]);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const raf = requestAnimationFrame(recheck);
    el.addEventListener("scroll", recheck, { passive: true });
    window.addEventListener("resize", recheck);
    let ro; if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(recheck); ro.observe(el); }
    return () => { cancelAnimationFrame(raf); el.removeEventListener("scroll", recheck); window.removeEventListener("resize", recheck); if (ro) ro.disconnect(); };
  }, [ref, recheck]);
  return { ...state, scrollPrev, scrollNext, recheck };
}

export default function RfmTableOffcanvas({
  open = false,
  onClose,

  table = null,
  zones = [],
  statuses = [],

  //Objects are other elements in the floor plan that are not tables, like bar, cashier, etc, these objects can also have an offcanvas to edit their configuration, but they don't have orders or reservation data, so we pass them as a separate prop, if the object has orders or reservation data, we can pass it in the table prop and ignore the object prop
  object = null,
  onDeleteObject,

  // Acciones base (las maneja App)
  onUpdateTable, // (tableId, patch) => void
  onDeleteTable, // (tableId) => void

  // Para abrir modales externos (si ya los tienes)
  onOpenModal, // (name, payload?) => void

  //To get reservation data by id, this is used to load the reservation data in the reservation tab when the table has a reservation_id but not the reservation data
  getReservationData, // async (reservation_id) => { name, phone, guests, date, time, notes }
  onCloseTableSession,
  onMoveTableSession,
  processingSessionId,
  prepayEnabled = false,
  // "Pagar con POS": (table) => void - hands off table.posPaymentRequest to the caja screen so the
  // waiter can charge it with the usual card integrations (BAC/Banesco/Evertech/KinPOS).
  onChargePosPayment,
}) {
  const [activeTab, setActiveTab] = useState("orders");
  const [paymentAmount, setPaymentAmount] = useState("");

  // Modo de presentación del panel: 'dock' (anclado) | 'modal' (centrado). Solo UI.
  const [panelMode, setPanelMode] = useState("dock");

  // Config form state
  const [configNumber, setConfigNumber] = useState("");
  const [configChairs, setConfigChairs] = useState(2);
  const [configShape, setConfigShape] = useState("square");
  const [configWidth, setConfigWidth] = useState(1);
  const [configHeight, setConfigHeight] = useState(1);
  const [configZone, setConfigZone] = useState("");

  // Reservation form state
  const [resName, setResName] = useState("");
  const [resPhone, setResPhone] = useState("");
  const [resGuests, setResGuests] = useState(2);
  const [resDate, setResDate] = useState("");
  const [resTime, setResTime] = useState("");
  const [resNotes, setResNotes] = useState("");
  const [statusOpenState, setStatusOpenState] = useState(false); // colapsado por defecto
  // Reff to ovoid the useEffect to run when we open the offcanvas for the same table, this is because when we update the table, the table data changes and the useEffect runs again, but we only want it to run when we open the offcanvas or when we change to a different table
  const lastTableIdRef = useRef(null);
  const lastOpenRef = useRef(false);
  const [showPrepayInfo, setShowPrepayInfo] = useState(false);
  // flechas de las tabs
  const tabsRef = useRef(null);
  const { showArrows, canPrev, canNext, scrollPrev, scrollNext, recheck } = useScrollArrows(tabsRef);


  const [confirmCloseSession, setConfirmCloseSession] = useState(false);




  useEffect(() => {
    if (!open || (!table && !object)) return;

    const currentId = table ? table.id : object.id;
    const wasClosed = !lastOpenRef.current;
    const entityChanged = lastTableIdRef.current !== currentId;

    //Just reset tab / form when: 
    // - We open the offcanvas for a different table

    if (wasClosed || entityChanged) {
      if (table) {
        setActiveTab("orders");
        setPaymentAmount("");

        setConfigNumber(table.number ?? "");
        setConfigChairs(Number(table.chairs ?? 2));
        setConfigShape(table.shape ?? "square");
        setConfigWidth(Number(table.gridW ?? 1));
        setConfigHeight(Number(table.gridH ?? 1));
        setConfigZone(table.zone ?? (zones?.[0]?.id || ""));

        if (table.reservation) {
          setResName(table.reservation.name || "");
          setResPhone(table.reservation.phone || "");
          setResGuests(Number(table.reservation.guests || 2));
          setResDate(table.reservation.date || "");
          setResTime(table.reservation.time || "");
          setResNotes(table.reservation.notes || "");
        } else {
          if (table.reservation_id && table.reservation_id !== null) {
            handleReservationData(table.reservation_id);
          } else {
            setResName("");
            setResPhone("");
            setResGuests(2);
            setResDate("");
            setResTime("");
            setResNotes("");
          }
        }
      }

      if (object) {
        setActiveTab("config");
        setConfigWidth(Number(object.gridW ?? 1));
        setConfigHeight(Number(object.gridH ?? 1));
        setConfigZone(object.zone ?? (zones?.[0]?.id || ""));
      }
    }

    lastTableIdRef.current = currentId;
    lastOpenRef.current = open;
  }, [open, table, object, zones]);

  // recalcular flechas al abrir, cambiar de modo o de entidad
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(recheck, 220);
    return () => clearTimeout(t);
  }, [open, panelMode, table, object, recheck]);

  const zone = useMemo(() => {
    if (!table) return null;
    return zones.find((z) => z.id === table.zone) || null;
  }, [table, zones]);

  const tableOrders = useMemo(() => {
    return Array.isArray(table?.table_orders) ? table.table_orders : [];
  }, [table]);

  const tableOrderOptions = useMemo(() => {
    return (table?.table_orders || []).map((ord) => ({
      id: ord.id,
      label: `Pedido #${formatOrderCode(ord)} - $${Number(ord.total || 0).toFixed(2)}`,
    }));
  }, [table]);

  const activeOrder = useMemo(() => {
    if (!Array.isArray(table?.table_orders) || table.table_orders.length === 0) return null;
    return table.table_orders[0];
  }, [table]);
  const tableSession = table?.tableSession || null;
  // The channel (source) badge used to show the literal backend value "UNKNOWN" whenever the
  // session's own source field wasn't set - showing who actually opened the session (the anfitrion,
  // same participant labeled below in "Participantes") is more useful to staff than that.
  const tableSessionHostName = useMemo(() => {
    const participants = tableSession?.participants_summary || [];
    const host = participants.find((p) => p.role === "PRIMARY") || participants[0];
    return host?.display_name || "Cliente";
  }, [tableSession]);

  const total = Number(table?.total || 0);
  const paid = Number(table?.paid || 0);

  const objectZone = useMemo(() => {
    if (!object) return null;
    return zones.find((z) => z.id === object.zone) || null;
  }, [object, zones]);


  if (!open || (!table && !object)) return null;

  // =========================
  // HANDLERS
  // =========================
  const handleStatusChange = (statusId) => {
    let patch = { status: statusId };

    // si vuelve a disponible, limpiamos data como en tu componente
    if (statusId === "available") {
      patch.guests = 0;
      patch.total = 0;
      patch.paid = 0;
      patch.time = null;
      patch.orders = [];
      patch.comensales = [];
      patch.reservation = null;
    }

    if (statusId === "occupied" || statusId === "reserved") {
      const now = new Date();
      //We format the date to yyyy-mm-dd hh:mm format but with the time zone of panama, but we need to convert the date to ISO string first
      const formattedDate = new Intl.DateTimeFormat("sv-SE", {
        timeZone: "America/Panama",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(now).replace(" ", " ");
      //We add the formatted date to the patch, this will be used to calculate the elapsed time in the backend
      patch.status_since = formattedDate;
    } else {
      patch.status_since = null;
    }

    onUpdateTable?.(table.id, patch);
  };

  const handleAddPayment = () => {
    const amount = Number(paymentAmount) || 0;
    if (amount <= 0) return;

    const newPaid = paid + amount;
    const newRemaining = total - newPaid;

    if (newRemaining <= 0 && total > 0) {
      // paga y limpia mesa
      onUpdateTable?.(table.id, {
        paid: newPaid,
        status: "available",
        guests: 0,
        total: 0,
        time: null,
        orders: [],
        comensales: [],
      });
      setPaymentAmount("");
      onClose?.();
      return;
    }

    onUpdateTable?.(table.id, { paid: newPaid });
    setPaymentAmount("");
  };

  const handlePayFull = () => {
    if (total <= 0) return;

    onUpdateTable?.(table.id, {
      paid: total,
      status: "available",
      guests: 0,
      total: 0,
      time: null,
      orders: [],
      comensales: [],
    });
    onClose?.();
  };

  const handleSaveReservation = () => {
    const now = new Date();
    const isoString = now.toISOString();
    const formattedDate = isoString.replace("T", " ").substring(0, 16);

    const patch = {
      reservation: {
        name: resName,
        phone: resPhone,
        guests: resGuests,
        date: resDate,
        time: resTime,
        notes: resNotes,
      },
      status_since: formattedDate,
    };

    if (table.status === "available") patch.status = "reserved";

    onUpdateTable?.(table.id, patch);
    onClose?.();
  };

  const handleSaveConfig = () => {
    onUpdateTable?.(table.id, {
      number: String(configNumber ?? "").trim(),
      name: String(configNumber ?? "").trim() || `Mesa ${table.id}`,//This is for backward compatibility, in case the number field is empty, we use the id as name
      chairs: Math.max(1, Number(configChairs || 1)),
      shape: configShape,
      gridW: Math.max(1, Number(configWidth || 1)),
      gridH: Math.max(1, Number(configHeight || 1)),
      zone: Number(configZone) || table.zone,
      branchoffice_environment_id: Number(configZone) || table.zone, // For backend we send branchoffice_environment_id instead of zone, but in the frontend we use zone for simplicity
    });
    onClose?.();
  };

  // Handle to delete a table.
  const handleDeleteTable = () => {
    onDeleteTable?.(table.id);
    onClose?.();
  };

  const handleDeleteObject = () => {
    onDeleteObject?.(object.id);
    onClose?.();
  };

  const handleAddComensal = () => {
    if (!tableOrderOptions.length) return;

    onOpenModal?.("add-comensal", {
      tableId: table.id,
      orderOptions: tableOrderOptions,
      preselectedOrderId: tableOrderOptions[0]?.id || null,
    });
  };

  const handleAddOrder = () => onOpenModal?.("add-order", { tableId: table.id });
  const handleSplitBill = () => onOpenModal?.("split-bill", { tableId: table.id });
  const handleOpenProductModal = () => onOpenModal?.("add-products", { tableId: table.id });
  // Function to fetch reservation data by id and load it in the form, this is used when the table has a reservation_id but not the reservation data, we try to fetch the reservation data and load it in the form
  const handleReservationData = async () => {
    try {
      const reservationData = await getReservationData(table.reservation_id);
      setResName(reservationData.guest_name || "");
      setResPhone(reservationData.guest_phone || "");
      setResGuests(reservationData.people_qty || 2);
      setResDate(reservationData.reserved_at || "");
      setResTime(reservationData.reserved_at || "");
      setResNotes(reservationData.notes || "");
    } catch (err) {
      console.error("Error fetching reservation data for table:", err);
    }
  }

  const isModal = panelMode === "modal";

  // =========================
  // UI
  // =========================
  return (
    <>
      <div className={"off-x-offcanvas-backdrop" + (isModal ? " is-modal" : "")} onClick={onClose} />

      <div className={"off-x-offcanvas" + (isModal ? " is-modal" : "")}>
        {/* HEADER */}
        <div className="off-x-offcanvas-header">
          <div>
            <div className="off-x-offcanvas-title">
              {table ? `Mesa ${table.number}` : `Objeto ${object?.id}`}
            </div>

            <div className="off-x-offcanvas-subtitle">
              {table
                ? `Zona ${zone?.name || "—"} • ${table.chairs} sillas`
                : `Zona ${objectZone?.name || "—"} • ${object?.type || "objeto"}`}
            </div>
          </div>

          <div className="off-x-offcanvas-head-actions">
            {/* <button
              className="off-x-offcanvas-mode"
              onClick={() => setPanelMode((m) => (m === "dock" ? "modal" : "dock"))}
              type="button"
              title={isModal ? "Anclar a la derecha" : "Ver como ventana"}
            >
              {isModal ? <IcoDock /> : <IcoWindow />} */}
            {/* </button> 
    */}
            <button className="ord-btn ord-btn-secondary ord-btn-icon" type="button" onClick={onClose} title="Cerrar">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="off-x-offcanvas-tabs-wrap">
          <button className="off-x-tabs-arrow" type="button" aria-label="Anterior" hidden={!showArrows} disabled={!canPrev} onClick={scrollPrev}>
            <IcoChevL />
          </button>

          <div className="off-x-offcanvas-tabs" ref={tabsRef}>
            {table ? (
              <>
                <button className={`off-x-tab ${activeTab === "orders" ? "active" : ""}`} onClick={() => setActiveTab("orders")}>
                  Pedidos
                </button>
                {/* <button className={`off-x-tab ${activeTab === "comensales" ? "active" : ""}`} onClick={() => setActiveTab("comensales")}>
                  Comensales
                </button> */}
                <button className={`off-x-tab ${activeTab === "transactions" ? "active" : ""}`} onClick={() => setActiveTab("transactions")}>
                  Transacciones
                </button>
                {/*  <button className={`off-x-tab ${activeTab === "reservation" ? "active" : ""}`} onClick={() => setActiveTab("reservation")}>
                  Reservación
                </button> */}
                <button className={`off-x-tab ${activeTab === "config" ? "active" : ""}`} onClick={() => setActiveTab("config")}>
                  Config
                </button>
              </>
            ) : (
              <button className="off-x-tab active" onClick={() => setActiveTab("config")}>
                Config
              </button>
            )}
          </div>

          <button className="off-x-tabs-arrow" type="button" aria-label="Siguiente" hidden={!showArrows} disabled={!canNext} onClick={scrollNext}>
            <IcoChevR />
          </button>
        </div>

        {/* BODY */}
        <div className="off-x-offcanvas-body">
          {/* ===================== ORDERS TAB ===================== */}
          {activeTab === "orders" && (
            <>
              {/* ESTADO */}
              <div className="off-x-detail-section">
                <button
                  type="button"
                  className="off-x-collapse-head"
                  onClick={() => setStatusOpenState((v) => !v)}
                  aria-expanded={statusOpenState}
                >
                  <div className="off-x-detail-section-title">
                    <i className="bi bi-circle-fill" /> Estado de la mesa
                  </div>

                  {/* resumen visible cuando está colapsado */}
                  <span className="off-x-collapse-summary">
                    {statuses.find((s) => s.id === table?.status)?.name || "Sin estado"}
                  </span>

                  <i className={`bi bi-chevron-down off-x-collapse-chev ${statusOpenState ? "is-open" : ""}`} />
                </button>

                <div className={`off-x-collapse-body ${statusOpenState ? "is-open" : ""}`}>
                  <div className="off-x-status-grid">
                    {statuses.slice(0, 3).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        data-status={s.id}
                        className={`off-x-status-card ${table?.status === s.id ? "active" : ""}`}
                        onClick={() => handleStatusChange(s.id)}
                      >
                        <div className="off-x-status-card-icon">
                          <i className={s.icon} />
                        </div>
                        <div className="off-x-status-card-name">{s.name}</div>
                      </button>
                    ))}

                  </div>
                  {/* METRICS */}

                  <div className={`off-x-collapse-body ${statusOpenState ? "is-open" : ""}`}>
                    <div className="off-x-detail-section">
                      <div className="off-x-metrics">
                        <div className="off-x-metric">
                          <div className="off-x-metric-value">{table?.chairs || 0}</div>
                          <div className="off-x-metric-label">Sillas</div>
                        </div>

                        <div className="off-x-metric">
                          <div className="off-x-metric-value">{table?.guests || 0}</div>
                          <div className="off-x-metric-label">Personas</div>
                        </div>

                        <div className="off-x-metric">
                          <div className="off-x-metric-value"><ElapsedClock since={table?.elapsed_since} fallback="00:00" /></div>
                          <div className="off-x-metric-label">Tiempo</div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>



              {tableSession ? (
                <div className="off-x-detail-section off-x-session-offcanvas-section">
                  <div className="off-x-detail-section-title-with-button">
                    <span><i className="bi bi-phone" /> Sesion e-commerce</span>
                    <span className="off-x-session-source">
                      {tableSessionHostName}
                    </span>
                  </div>
                  <div className="off-x-session-info-grid">
                    <div>
                      <span>Estado</span>
                      <strong>{tableSession.status || "-"}</strong>
                    </div>
                    <div>
                      <span>Solicitud</span>
                      <strong>{tableSession.requested_at ? new Date(tableSession.requested_at).toLocaleTimeString() : "-"}</strong>
                    </div>
                    <div>
                      <span>Aprobacion</span>
                      <strong>{tableSession.approved_at ? new Date(tableSession.approved_at).toLocaleTimeString() : "-"}</strong>
                    </div>
                    <div>
                      <span>{tableOrders.length > 1 ? "Pedidos" : "Pedido"}</span>
                      <strong>
                        {tableOrders.length > 0
                          ? tableOrders.map((ord) => `#${formatOrderCode(ord)}`).join(", ")
                          : "Sin pedidos"}
                      </strong>
                    </div>
                  </div>
                  {(table?.posPaymentRequests || []).map((request) => (
                    <div
                      key={request.payment_attempt_id}
                      className="off-x-detail-section-title-with-button"
                      style={{ marginTop: 8, background: "#fff7e6", padding: "8px 10px", borderRadius: 8 }}
                    >
                      <span>
                        <i className="bi bi-credit-card-2-front" /> Solicitud de pago POS{request.participant?.display_name ? ` · ${request.participant.display_name}` : ""}: ${Number(request.amount || 0).toFixed(2)}
                        {" "}({(request.items || []).length} producto{(request.items || []).length === 1 ? "" : "s"})
                      </span>
                      <button
                        className="off-x-btn off-x-btn-primary"
                        type="button"
                        onClick={() => onChargePosPayment?.(table, request)}
                      >
                        <i className="bi bi-credit-card" /> Cobrar en terminal
                      </button>
                    </div>
                  ))}
                  <div className="off-x-session-offcanvas-actions">
                    {tableSession.temp_order_id ? (
                      <button
                        className="off-x-btn off-x-btn-secondary"
                        type="button"
                        onClick={() => onOpenModal?.("order-details", { tableId: table.id, orderId: tableSession.temp_order_id })}
                      >
                        <i className="bi bi-receipt" /> Abrir orden
                      </button>
                    ) : null}
                    {onMoveTableSession && ["APPROVED", "ACTIVE"].includes(String(tableSession.status || "").toUpperCase()) ? (
                      <button
                        className="off-x-btn off-x-btn-secondary"
                        type="button"
                        disabled={processingSessionId === tableSession.session_uuid}
                        onClick={() => onMoveTableSession(table)}
                      >
                        <i className="bi bi-arrow-left-right" /> Mover de mesa
                      </button>
                    ) : null}



                  </div>
                  <div className="d-flex justify-content-center w-100" >
                    {/* <button
                      className="off-x-btn off-x-btn-secondary"
                      type="button"
                      disabled={processingSessionId === tableSession.session_uuid}
                      onClick={() => onCloseTableSession?.(table)}
                    >
                      <i className="bi bi-x-circle" /> Cerrar sesion
                    </button> */}

                    <div className="d-flex justify-content-end w-100 mt-2">
                      <button
                        className=" text-secondary py-1 px-2 rounded-pill btn-sm border border-secondary border-0 opacity-50"
                        type="button"
                        disabled={processingSessionId === tableSession.session_uuid}
                        onClick={() => setConfirmCloseSession(true)}
                      >
                        <i className="bi bi-exit" /> Cerrar sesión
                      </button>
                    </div>

                  </div>
                  {confirmCloseSession ? createPortal(
                    <div className="off-x-confirm-ovl" onClick={() => setConfirmCloseSession(false)}>
                      <div className="off-x-confirm" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
                        <div className="off-x-confirm-icon">
                          <i className="bi bi-exclamation-triangle-fill" />
                        </div>

                        <div className="off-x-confirm-title">¿Cerrar la sesión del cliente?</div>
                        <div className="off-x-confirm-text">
                          Esto cerrará la sesión de e-commerce que el cliente tiene abierta en esta mesa.
                          El cliente ya no podrá seguir pidiendo desde su teléfono. Esta acción no se puede deshacer.
                        </div>

                        <div className="off-x-confirm-actions">
                          <button
                            className="off-x-btn off-x-btn-secondary"
                            type="button"
                            onClick={() => setConfirmCloseSession(false)}
                          >
                            Cancelar
                          </button>
                          <button
                            className="off-x-btn off-x-btn-danger"
                            type="button"
                            disabled={processingSessionId === tableSession.session_uuid}
                            onClick={() => {
                              setConfirmCloseSession(false);
                              onCloseTableSession?.(table);
                            }}
                          >
                            <i className="bi bi-x-circle" /> Sí, cerrar sesión
                          </button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  ) : null}
                </div>
              ) : prepayEnabled ? (
                // Only shown when the table has no open session at all - generating a fresh prepay
                // QR for a table that's mid-session would just fail server-side (see
                // generatePrepayQr's assertTableNotOccupied guard on the backend), so it never makes
                // sense to offer this here otherwise.
                <div className="off-x-detail-section off-x-session-offcanvas-section">
                  <div className="off-x-detail-section-title-with-button">
                    <span><i className="bi bi-qr-code" /> Pre-pago</span>

                    <div className="off-x-info-wrap">
                      <button
                        type="button"
                        className="off-x-info-btn"
                        aria-label="Qué es el pre-pago"
                        aria-expanded={showPrepayInfo}
                        onClick={() => setShowPrepayInfo((v) => !v)}
                      >
                        <i className="bi bi-info-circle" />
                      </button>

                      {showPrepayInfo ? (
                        <>
                          {/* capa para cerrar al hacer clic fuera */}
                          <div className="off-x-info-overlay" onClick={() => setShowPrepayInfo(false)} />
                          <div className="off-x-info-pop" role="dialog">
                            <div className="off-x-info-pop-head">
                              <span>¿Qué es el pre-pago?</span>
                              <button type="button" className="off-x-info-pop-close" onClick={() => setShowPrepayInfo(false)} aria-label="Cerrar">
                                <i className="bi bi-x-lg" />
                              </button>
                            </div>
                            <p className="off-x-info-pop-text">
                              Genera un QR de un solo uso para que el cliente escanee y realice su pedido.
                              El pedido aparecerá aquí como cualquier otra mesa.
                              Cuando el cliente pague, se enviará automáticamente a cocina.
                            </p>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="off-x-session-offcanvas-actions">
                    <button
                      className="off-x-btn off-x-btn-primary"
                      type="button"
                      onClick={() => onOpenModal?.("prepay-qr", { tableId: table.id, tableName: table?.number })}
                    >
                      <i className="bi bi-qr-code" /> Generar QR para pedir y pagar
                    </button>
                  </div>
                </div>
              ) : (
                // Pospago (branch not prepay-enabled): the table's QR is persistent - it points to
                // the request-access flow (a waiter still has to approve), not a one-time order.
                <div className="off-x-detail-section off-x-session-offcanvas-section">
                  <div className="off-x-detail-section-title-with-button">
                    <span><i className="bi bi-qr-code" /> QR de mesa</span>
                  </div>
                  <p className="off-x-prepay-hint">
                    Genera el QR para que el cliente escanee, solicite acceso y un mesero apruebe la sesión.
                  </p>
                  <div className="off-x-session-offcanvas-actions">
                    <button
                      className="off-x-btn off-x-btn-primary"
                      type="button"
                      onClick={() => onOpenModal?.("pospago-qr", { tableId: table.id, tableName: table?.number })}
                    >
                      <i className="bi bi-qr-code" /> Generar QR de mesa
                    </button>


                  </div>
                </div>


              )}

              {/* Fase 3/5: participantes de la sesion e-commerce (participants_summary /
                  lines_by_participant vienen de GET /table-sessions/table/:table_id/active - ver
                  controller/tableSessions.js#getActiveByTable en el backend). Solo lectura: el POS
                  no genera ni modifica participantes desde aqui (Fase 9). No se muestra participant_uuid
                  ni ningun token, solo nombre/rol/estado/cantidad/total. */}
              {tableSession && Array.isArray(tableSession.participants_summary) && tableSession.participants_summary.length > 0 ? (
                <div className="off-x-detail-section off-x-session-offcanvas-section">
                  <div className="off-x-detail-section-title">
                    <i className="bi bi-people" /> Participantes ({tableSession.participant_count || tableSession.participants_summary.length})
                  </div>
                  <div className="off-x-participants-list">
                    {tableSession.participants_summary.map((participant) => {
                      const linesEntry = (tableSession.lines_by_participant || []).find(
                        (entry) => entry.participant_uuid === participant.participant_uuid
                      );
                      const items = linesEntry?.items || [];
                      const itemCount = items.length;
                      const participantTotal = items.reduce(
                        (sum, item) => sum + Number(item.price || 0) * Number(item.amount || 0),
                        0
                      );

                      return (
                        <div key={participant.participant_uuid} className="off-x-participant-row">
                          <div className="off-x-participant-name">
                            <i className="bi bi-person-circle" /> {participant.display_name || "Cliente"}
                            {participant.role === "PRIMARY" ? (
                              <span className="off-x-participant-role">Anfitrion</span>
                            ) : participant.role === "GUEST" ? (
                              <span className="off-x-participant-role">Invitado</span>
                            ) : null}
                          </div>
                          <div className="off-x-participant-meta">
                            <span>{participant.status || "ACTIVE"}</span>
                            <span>{itemCount} producto{itemCount === 1 ? "" : "s"}</span>
                            {participantTotal > 0 ? <span>${participantTotal.toFixed(2)}</span> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* PEDIDOS */}
              <div className="off-x-detail-section">
                <div className="off-x-detail-section-title-with-button">
                  <span>
                    <i className="bi bi-bag" /> Pedidos abiertos
                  </span>
                </div>



                {tableOrders.length === 0 ? (
                  <button className="off-x-add-comensal-btn" type="button" onClick={handleOpenProductModal}>
                    <i className="bi bi-plus-circle" /> Agregar orden
                  </button>
                ) : null}

                <div className="off-x-orders-list">
                  {tableOrders.length === 0 ? (
                    <div className="off-x-empty">Sin pedidos</div>
                  ) : (
                    tableOrders.map((ord, i) => (
                      <button
                        key={ord.id}
                        type="button"
                        className="off-x-order-row"
                        style={{ width: "100%", textAlign: "left", background: "transparent", border: "none" }}
                        onClick={() => onOpenModal?.("order-details", { tableId: table.id, orderId: ord.id })}
                      >
                        <div className="off-x-order-left">
                          <div className="off-x-order-emoji">🧾</div>
                          <div>
                            <div className="off-x-order-name">
                              Pedido #{formatOrderCode(ord)}
                              {ord.order_source === "ECOMMERCE_TABLE" ? <span className="off-x-order-source-badge">Pedido del cliente</span> : null}
                            </div>
                            <div className="off-x-order-sub">
                              {(ord.items || []).length} items • {ord.status || "open"}
                            </div>
                          </div>
                        </div>

                        <div className="off-x-order-right">
                          <div style={{ fontWeight: 900, color: "var(--accent-primary)" }}>
                            ${(Number(ord.total || 0)).toFixed(2)}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

            </>
          )}

          {/* ===================== COMENSALES TAB ===================== */}
          {activeTab === "comensales" && (
            <div className="off-x-detail-section">
              <div className="off-x-detail-section-title">
                <i className="bi bi-people" /> Comensales en la Mesa
              </div>

              <div className="off-x-comensales-list">
                {(!table.comensales || table.comensales.length === 0) ? (
                  <div className="off-x-empty">No hay comensales registrados</div>
                ) : (
                  table.comensales.map((c, idx) => {
                    const comensalTotal = (c.orders || []).reduce(
                      (s, o) => s + (Number(o.price || 0) * Number(o.qty || 1)),
                      0
                    );

                    return (
                      <div key={c.diner_id || idx} className="off-x-comensal-card">
                        <div className="off-x-comensal-header">
                          <div className="off-x-comensal-name">
                            <i className="bi bi-person-circle" /> {c.name}
                          </div>
                          <div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>
                            {(() => {
                              const selected = tableOrderOptions.find((o) => String(o.id) === String(c.selected_order_id));
                              return selected ? selected.label : "Sin orden seleccionada";
                            })()}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span className="off-x-comensal-total">${comensalTotal.toFixed(2)}</span>

                            <div className="off-x-comensal-actions">
                              <button className="off-x-comensal-action" type="button" title="Agregar platillo"
                                onClick={() =>
                                  onOpenModal?.("add-order-comensal", {
                                    tableId: table.id,
                                    orderId: c.selected_order_id || tableOrderOptions[0]?.id || null,
                                    orderOptions: tableOrderOptions,
                                    dinerId: c.diner_id,
                                    dinerName: c.name,
                                  })
                                }
                              >
                                <i className="bi bi-plus" />
                              </button>
                              <button className="off-x-comensal-action" type="button" title="Eliminar comensal"
                                onClick={() =>
                                  onOpenModal?.("remove-comensal", {
                                    tableId: table.id,
                                    orderId: c.selected_order_id || activeOrder?.id || null,
                                    dinerId: c.diner_id,
                                    dinerName: c.name,
                                  })
                                }
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="off-x-comensal-orders">
                          {(c.orders || []).length === 0 ? (
                            <div style={{ fontSize: ".75rem", color: "var(--text-muted)", padding: "6px" }}>Sin pedidos</div>
                          ) : (
                            (c.orders || []).map((o, oi) => (
                              <div key={oi} className="off-x-comensal-order">
                                <div className="off-x-comensal-order-name">
                                  <span>{o.emoji || "🍽️"}</span>
                                  <span>
                                    {o.name} x{o.qty || 1}
                                  </span>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className="off-x-comensal-order-price">
                                    ${(Number(o.price || 0) * Number(o.qty || 1)).toFixed(2)}
                                  </span>

                                  <button
                                    className="off-x-comensal-action"
                                    type="button"
                                    title="Quitar"
                                    onClick={() =>
                                      onOpenModal?.("remove-order-comensal", {
                                        tableId: table.id,
                                        dinerId: c.diner_id,
                                        dinerName: c.name,
                                        orderId: o.order_id,
                                        source: o.source,
                                        groupId: o.group_id || null,
                                        comboId: o.combo_id || null,
                                        itemName: o.name,
                                      })
                                    }
                                    style={{ width: 22, height: 22 }}
                                  >
                                    <i className="bi bi-x" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <button className="off-x-add-comensal-btn" type="button" onClick={handleAddComensal}>
                <i className="bi bi-plus-circle" /> Agregar Comensal
              </button>
            </div>
          )}

          {/* ===================== RESERVATION TAB ===================== */}
          {/* ===================== TRANSACTIONS TAB ===================== */}
          {activeTab === "transactions" && table && <TableTransactionsTab table={table} />}

          {activeTab === "reservation" && (
            <>
              <div className="off-x-form-group">
                <label className="off-x-form-label">Cliente</label>
                <input className="off-x-form-input" placeholder="Nombre completo" value={resName} onChange={(e) => setResName(e.target.value)} />
              </div>

              <div className="off-x-form-row">
                <div className="off-x-form-group">
                  <label className="off-x-form-label">Teléfono</label>
                  <input className="off-x-form-input" placeholder="+507 ..." value={resPhone} onChange={(e) => setResPhone(e.target.value)} />
                </div>

                <div className="off-x-form-group">
                  <label className="off-x-form-label">Personas</label>
                  <input className="off-x-form-input" type="number" min="1" value={resGuests} onChange={(e) => setResGuests(Number(e.target.value) || 1)} />
                </div>
              </div>

              <div className="off-x-form-row">
                <div className="off-x-form-group">
                  <label className="off-x-form-label">Fecha</label>
                  <input className="off-x-form-input" type="date" value={resDate} onChange={(e) => setResDate(e.target.value)} />
                </div>

                <div className="off-x-form-group">
                  <label className="off-x-form-label">Hora</label>
                  <input className="off-x-form-input" type="time" value={resTime} onChange={(e) => setResTime(e.target.value)} />
                </div>
              </div>

              <div className="off-x-form-group">
                <label className="off-x-form-label">Notas</label>
                <textarea
                  className="off-x-form-input off-x-form-textarea"
                  placeholder="Alergias, ocasión especial..."
                  value={resNotes}
                  onChange={(e) => setResNotes(e.target.value)}
                />
              </div>

              <button className="off-x-btn off-x-btn-primary" type="button" style={{ width: "100%" }} onClick={handleSaveReservation}>
                <i className="bi bi-check-lg" /> Guardar Reservación
              </button>
            </>
          )}

          {/* ===================== CONFIG TAB ===================== */}
          {activeTab === "config" && (
            <>
              {table ? (
                <>
                  <div className="off-x-form-group">
                    <label className="off-x-form-label">Nombre de Mesa</label>
                    <input className="off-x-form-input" value={configNumber} onChange={(e) => setConfigNumber(e.target.value)} />
                  </div>

                  <div className="off-x-form-group">
                    <label className="off-x-form-label">Número de Sillas</label>
                    <div className="off-x-chair-selector">
                      <button className="off-x-chair-btn" type="button" onClick={() => setConfigChairs((v) => Math.max(1, Number(v || 1) - 1))}>
                        <i className="bi bi-dash" />
                      </button>
                      <span className="off-x-chair-count">{configChairs}</span>
                      <button className="off-x-chair-btn" type="button" onClick={() => setConfigChairs((v) => Number(v || 1) + 1)}>
                        <i className="bi bi-plus" />
                      </button>
                    </div>
                  </div>

                  <div className="off-x-form-group">
                    <label className="off-x-form-label">Forma</label>
                    <div className="off-x-shape-selector">
                      <button
                        type="button"
                        className={`off-x-shape-option shape-square ${configShape === "square" ? "active" : ""}`}
                        onClick={() => setConfigShape("square")}
                      >
                        Cuadrada
                      </button>
                      <button
                        type="button"
                        className={`off-x-shape-option shape-rectangle ${configShape === "rectangle" ? "active" : ""}`}
                        onClick={() => setConfigShape("rectangle")}
                      >
                        Rectangular
                      </button>
                      <button
                        type="button"
                        className={`off-x-shape-option shape-round ${configShape === "round" ? "active" : ""}`}
                        onClick={() => setConfigShape("round")}
                      >
                        Redonda
                      </button>
                    </div>
                  </div>

                  <div className="off-x-form-group">
                    <label className="off-x-form-label">Tamaño en Grid (celdas)</label>
                    <div className="off-x-size-selector">
                      <div className="off-x-size-group">
                        <label>Ancho:</label>
                        <input type="number" min="1" max="10" value={configWidth} onChange={(e) => setConfigWidth(Number(e.target.value) || 1)} />
                      </div>
                      <div className="off-x-size-group">
                        <label>Alto:</label>
                        <input type="number" min="1" max="10" value={configHeight} onChange={(e) => setConfigHeight(Number(e.target.value) || 1)} />
                      </div>
                    </div>
                  </div>

                  <div className="off-x-form-group">
                    <label className="off-x-form-label">Zona</label>
                    <select className="off-x-form-input" value={configZone} onChange={(e) => setConfigZone(e.target.value)}>
                      {zones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button className="off-x-btn off-x-btn-primary" type="button" style={{ width: "100%", marginBottom: 10 }} onClick={handleSaveConfig}>
                    <i className="bi bi-check-lg" /> Guardar Cambios
                  </button>

                  <button className="off-x-btn off-x-btn-secondary" type="button" style={{ width: "100%", color: "#dc2626" }} onClick={handleDeleteTable}>
                    <i className="bi bi-trash" /> Eliminar Mesa
                  </button>
                </>
              ) : object ? (
                <>
                  <div className="off-x-detail-section">
                    <div className="off-x-detail-section-title">
                      <i className="bi bi-bounding-box" /> Configuración del objeto
                    </div>

                    <div className="off-x-metrics" style={{ marginBottom: 12 }}>
                      <div className="off-x-metric">
                        <div className="off-x-metric-value">{object.type || "-"}</div>
                        <div className="off-x-metric-label">Tipo</div>
                      </div>

                      <div className="off-x-metric">
                        <div className="off-x-metric-value">{object.gridW || 1}x{object.gridH || 1}</div>
                        <div className="off-x-metric-label">Tamaño</div>
                      </div>
                    </div>

                    <button
                      className="off-x-btn off-x-btn-secondary"
                      type="button"
                      style={{ width: "100%", color: "#dc2626" }}
                      onClick={handleDeleteObject}
                    >
                      <i className="bi bi-trash" /> Eliminar Objeto
                    </button>
                  </div>
                </>
              ) : null}
            </>
          )}

        </div>

        {/* FOOTER */}
        {/* <div className="off-x-offcanvas-footer">


          <div className="d-flex justify-content-between gap-3">
            <button className="off-x-btn off-x-btn-secondary" type="button" onClick={onClose}>
              <i className="bi bi-x" /> Cerrar
            </button>
          </div>

        </div> */}
      </div>
    </>
  );
}